/**
 * SopRichEditor — Google Docs-style toolbar + content area for SOP editing.
 * Accepts a Tiptap editor instance and renders the full formatting toolbar.
 */
import { useState, useRef, useCallback } from "react";
import { type Editor, EditorContent } from "@tiptap/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Popover, PopoverContent, PopoverTrigger,
} from "@/components/ui/popover";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Bold, Italic, Underline as UnderlineIcon, Strikethrough, Code,
  Heading1, Heading2, Heading3, List, ListOrdered, Quote,
  Undo, Redo, Link2, ImageIcon, Table as TableIcon,
  AlignLeft, AlignCenter, AlignRight, AlignJustify,
  Highlighter, Upload, Minus,
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";

// ── Text colors ────────────────────────────────────────────────────────────────
const TEXT_COLORS = [
  "#000000", "#434343", "#666666", "#999999", "#B7B7B7", "#CCCCCC", "#D9D9D9",
  "#FF0000", "#FF4500", "#FF8C00", "#FFD700", "#00C000", "#008000", "#0000FF",
  "#4169E1", "#8B00FF", "#FF1493", "#FF69B4", "#00FFFF", "#20B2AA",
];

const HIGHLIGHT_COLORS = [
  "#FFFF00", "#00FF00", "#00FFFF", "#FF69B4", "#FFA500",
  "#FFD700", "#ADFF2F", "#7FFFD4", "#DDA0DD", "#FF6347",
];

// ── Font options ───────────────────────────────────────────────────────────────
const FONT_FAMILIES = [
  { label: "Default", value: "default" },
  { label: "Arial", value: "Arial" },
  { label: "Georgia", value: "Georgia" },
  { label: "Times New Roman", value: "Times New Roman" },
  { label: "Courier New", value: "Courier New" },
  { label: "Verdana", value: "Verdana" },
  { label: "Trebuchet MS", value: "Trebuchet MS" },
  { label: "Palatino", value: "Palatino Linotype" },
];

const FONT_SIZES = [
  "default", "10px", "11px", "12px", "14px", "16px", "18px",
  "20px", "24px", "28px", "32px", "36px", "48px", "64px",
];

// ── ToolbarButton ─────────────────────────────────────────────────────────────
function ToolbarBtn({
  active, onClick, title, children, disabled,
}: {
  active?: boolean;
  onClick: () => void;
  title: string;
  children: React.ReactNode;
  disabled?: boolean;
}) {
  return (
    <Button
      variant={active ? "secondary" : "ghost"}
      size="sm"
      onClick={onClick}
      title={title}
      disabled={disabled}
      className={`h-7 w-7 p-0 ${active ? "ring-1 ring-primary/30" : ""}`}
      type="button"
    >
      {children}
    </Button>
  );
}

// ── ColorPicker ────────────────────────────────────────────────────────────────
function ColorPicker({
  colors, onSelect, title, indicator,
}: {
  colors: string[];
  onSelect: (c: string) => void;
  title: string;
  indicator: React.ReactNode;
}) {
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="sm" title={title} className="h-7 px-1.5" type="button">
          {indicator}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-48 p-2" align="start">
        <div className="grid grid-cols-7 gap-1">
          {colors.map((c) => (
            <button
              key={c}
              className="w-5 h-5 rounded border border-border hover:scale-110 transition-transform"
              style={{ backgroundColor: c }}
              title={c}
              onClick={() => onSelect(c)}
              type="button"
            />
          ))}
        </div>
        <Separator className="my-2" />
        <Button
          variant="ghost"
          size="sm"
          className="w-full text-xs"
          onClick={() => onSelect("inherit")}
          type="button"
        >
          Remove color
        </Button>
      </PopoverContent>
    </Popover>
  );
}

// ── SopRichEditor ─────────────────────────────────────────────────────────────

interface SopRichEditorProps {
  editor: Editor | null;
  onImageUpload?: (file: File) => Promise<string>;
}

export function SopRichEditor({ editor, onImageUpload }: SopRichEditorProps) {
  const { toast } = useToast();
  const imageInputRef = useRef<HTMLInputElement>(null);
  const [showLinkDialog, setShowLinkDialog] = useState(false);
  const [linkUrl, setLinkUrl] = useState("");
  const [linkText, setLinkText] = useState("");

  // ── Link insertion ─────────────────────────────────────────────────────────
  const openLinkDialog = useCallback(() => {
    if (!editor) return;
    const prevUrl = editor.getAttributes("link").href;
    const selection = editor.state.doc.textBetween(
      editor.state.selection.from,
      editor.state.selection.to,
    );
    setLinkUrl(prevUrl || "");
    setLinkText(selection || "");
    setShowLinkDialog(true);
  }, [editor]);

  const applyLink = useCallback(() => {
    if (!editor) return;
    const url = linkUrl.trim();
    if (!url) {
      editor.chain().focus().extendMarkRange("link").unsetLink().run();
    } else {
      const href = url.startsWith("http") ? url : `https://${url}`;
      editor.chain().focus().extendMarkRange("link").setLink({ href }).run();
    }
    setShowLinkDialog(false);
  }, [editor, linkUrl]);

  // ── Image from URL ─────────────────────────────────────────────────────────
  const addImageByUrl = useCallback(() => {
    if (!editor) return;
    const src = window.prompt("Image URL:");
    if (src) editor.chain().focus().setImage({ src }).run();
  }, [editor]);

  // ── Image upload ───────────────────────────────────────────────────────────
  const handleImageUpload = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !editor) return;
    e.target.value = "";
    if (onImageUpload) {
      try {
        const src = await onImageUpload(file);
        editor.chain().focus().setImage({ src }).run();
      } catch {
        toast({ title: "Image upload failed", variant: "destructive" });
      }
    } else {
      // Fallback: base64 (limited but functional for SOPs)
      const reader = new FileReader();
      reader.onload = (ev) => {
        const src = ev.target?.result as string;
        if (src) editor.chain().focus().setImage({ src }).run();
      };
      reader.readAsDataURL(file);
    }
  }, [editor, onImageUpload, toast]);

  // ── Table insertion ────────────────────────────────────────────────────────
  const insertTable = useCallback(() => {
    if (!editor) return;
    editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run();
  }, [editor]);

  if (!editor) return null;

  const activeFont = editor.getAttributes("textStyle").fontFamily || "default";
  const activeSize = editor.getAttributes("textStyle").fontSize || "default";
  const activeColor = editor.getAttributes("textStyle").color;

  const sizeLabel = activeSize === "default" ? "Size" : activeSize.replace("px", "");

  return (
    <div className="border rounded-md overflow-hidden bg-background flex flex-col">
      {/* ── Toolbar ──────────────────────────────────────────────────────────── */}
      <div className="border-b bg-muted/30 px-2 py-1.5 flex flex-wrap gap-0.5 items-center">
        {/* Undo / Redo */}
        <ToolbarBtn onClick={() => editor.chain().focus().undo().run()} title="Undo" disabled={!editor.can().undo()}>
          <Undo className="w-3.5 h-3.5" />
        </ToolbarBtn>
        <ToolbarBtn onClick={() => editor.chain().focus().redo().run()} title="Redo" disabled={!editor.can().redo()}>
          <Redo className="w-3.5 h-3.5" />
        </ToolbarBtn>

        <Separator orientation="vertical" className="h-5 mx-1" />

        {/* Heading level */}
        <Select
          value={
            editor.isActive("heading", { level: 1 }) ? "h1"
            : editor.isActive("heading", { level: 2 }) ? "h2"
            : editor.isActive("heading", { level: 3 }) ? "h3"
            : editor.isActive("heading", { level: 4 }) ? "h4"
            : "p"
          }
          onValueChange={(v) => {
            if (v === "p") editor.chain().focus().setParagraph().run();
            else editor.chain().focus().toggleHeading({ level: Number(v.slice(1)) as 1 | 2 | 3 | 4 }).run();
          }}
        >
          <SelectTrigger className="h-7 w-[110px] text-xs" data-testid="select-sop-heading">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="p" className="text-sm">Paragraph</SelectItem>
            <SelectItem value="h1" className="text-xl font-bold">Heading 1</SelectItem>
            <SelectItem value="h2" className="text-lg font-bold">Heading 2</SelectItem>
            <SelectItem value="h3" className="text-base font-semibold">Heading 3</SelectItem>
            <SelectItem value="h4" className="text-sm font-semibold">Heading 4</SelectItem>
          </SelectContent>
        </Select>

        {/* Font Family */}
        <Select
          value={activeFont}
          onValueChange={(v) => {
            if (v === "default") editor.chain().focus().unsetFontFamily().run();
            else editor.chain().focus().setFontFamily(v).run();
          }}
        >
          <SelectTrigger className="h-7 w-[110px] text-xs" data-testid="select-sop-font-family">
            <SelectValue placeholder="Font" />
          </SelectTrigger>
          <SelectContent>
            {FONT_FAMILIES.map((f) => (
              <SelectItem key={f.value} value={f.value} style={{ fontFamily: f.value === "default" ? undefined : f.value }}>
                {f.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {/* Font Size */}
        <Select
          value={activeSize}
          onValueChange={(v) => {
            if (v === "default") (editor.chain().focus() as any).unsetFontSize().run();
            else (editor.chain().focus() as any).setFontSize(v).run();
          }}
        >
          <SelectTrigger className="h-7 w-[70px] text-xs" data-testid="select-sop-font-size">
            <SelectValue placeholder="Size">{sizeLabel}</SelectValue>
          </SelectTrigger>
          <SelectContent>
            {FONT_SIZES.map((s) => (
              <SelectItem key={s} value={s}>{s === "default" ? "Default" : s.replace("px", "")}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Separator orientation="vertical" className="h-5 mx-1" />

        {/* Basic formatting */}
        <ToolbarBtn active={editor.isActive("bold")} onClick={() => editor.chain().focus().toggleBold().run()} title="Bold (Ctrl+B)">
          <Bold className="w-3.5 h-3.5" />
        </ToolbarBtn>
        <ToolbarBtn active={editor.isActive("italic")} onClick={() => editor.chain().focus().toggleItalic().run()} title="Italic (Ctrl+I)">
          <Italic className="w-3.5 h-3.5" />
        </ToolbarBtn>
        <ToolbarBtn active={editor.isActive("underline")} onClick={() => editor.chain().focus().toggleUnderline().run()} title="Underline (Ctrl+U)">
          <UnderlineIcon className="w-3.5 h-3.5" />
        </ToolbarBtn>
        <ToolbarBtn active={editor.isActive("strike")} onClick={() => editor.chain().focus().toggleStrike().run()} title="Strikethrough">
          <Strikethrough className="w-3.5 h-3.5" />
        </ToolbarBtn>
        <ToolbarBtn active={editor.isActive("code")} onClick={() => editor.chain().focus().toggleCode().run()} title="Code">
          <Code className="w-3.5 h-3.5" />
        </ToolbarBtn>

        <Separator orientation="vertical" className="h-5 mx-1" />

        {/* Text color */}
        <ColorPicker
          colors={TEXT_COLORS}
          title="Text color"
          onSelect={(c) => {
            if (c === "inherit") editor.chain().focus().unsetColor().run();
            else editor.chain().focus().setColor(c).run();
          }}
          indicator={
            <span className="flex flex-col items-center gap-0.5">
              <span className="text-xs font-bold leading-none" style={{ color: activeColor || "currentColor" }}>A</span>
              <span className="w-4 h-1 rounded-sm" style={{ backgroundColor: activeColor || "#000" }} />
            </span>
          }
        />

        {/* Highlight */}
        <ColorPicker
          colors={HIGHLIGHT_COLORS}
          title="Highlight"
          onSelect={(c) => {
            if (c === "inherit") editor.chain().focus().unsetHighlight().run();
            else editor.chain().focus().toggleHighlight({ color: c }).run();
          }}
          indicator={<Highlighter className="w-3.5 h-3.5" />}
        />

        <Separator orientation="vertical" className="h-5 mx-1" />

        {/* Alignment */}
        <ToolbarBtn active={editor.isActive({ textAlign: "left" })} onClick={() => editor.chain().focus().setTextAlign("left").run()} title="Align Left">
          <AlignLeft className="w-3.5 h-3.5" />
        </ToolbarBtn>
        <ToolbarBtn active={editor.isActive({ textAlign: "center" })} onClick={() => editor.chain().focus().setTextAlign("center").run()} title="Align Center">
          <AlignCenter className="w-3.5 h-3.5" />
        </ToolbarBtn>
        <ToolbarBtn active={editor.isActive({ textAlign: "right" })} onClick={() => editor.chain().focus().setTextAlign("right").run()} title="Align Right">
          <AlignRight className="w-3.5 h-3.5" />
        </ToolbarBtn>
        <ToolbarBtn active={editor.isActive({ textAlign: "justify" })} onClick={() => editor.chain().focus().setTextAlign("justify").run()} title="Justify">
          <AlignJustify className="w-3.5 h-3.5" />
        </ToolbarBtn>

        <Separator orientation="vertical" className="h-5 mx-1" />

        {/* Lists */}
        <ToolbarBtn active={editor.isActive("bulletList")} onClick={() => editor.chain().focus().toggleBulletList().run()} title="Bullet list">
          <List className="w-3.5 h-3.5" />
        </ToolbarBtn>
        <ToolbarBtn active={editor.isActive("orderedList")} onClick={() => editor.chain().focus().toggleOrderedList().run()} title="Numbered list">
          <ListOrdered className="w-3.5 h-3.5" />
        </ToolbarBtn>
        <ToolbarBtn active={editor.isActive("blockquote")} onClick={() => editor.chain().focus().toggleBlockquote().run()} title="Blockquote">
          <Quote className="w-3.5 h-3.5" />
        </ToolbarBtn>
        <ToolbarBtn onClick={() => editor.chain().focus().setHorizontalRule().run()} title="Horizontal rule">
          <Minus className="w-3.5 h-3.5" />
        </ToolbarBtn>

        <Separator orientation="vertical" className="h-5 mx-1" />

        {/* Headings shortcut buttons */}
        <ToolbarBtn active={editor.isActive("heading", { level: 1 })} onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()} title="Heading 1">
          <Heading1 className="w-3.5 h-3.5" />
        </ToolbarBtn>
        <ToolbarBtn active={editor.isActive("heading", { level: 2 })} onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()} title="Heading 2">
          <Heading2 className="w-3.5 h-3.5" />
        </ToolbarBtn>
        <ToolbarBtn active={editor.isActive("heading", { level: 3 })} onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()} title="Heading 3">
          <Heading3 className="w-3.5 h-3.5" />
        </ToolbarBtn>

        <Separator orientation="vertical" className="h-5 mx-1" />

        {/* Links & media */}
        <ToolbarBtn active={editor.isActive("link")} onClick={openLinkDialog} title="Insert/edit link (Ctrl+K)">
          <Link2 className="w-3.5 h-3.5" />
        </ToolbarBtn>
        <ToolbarBtn onClick={addImageByUrl} title="Insert image from URL">
          <ImageIcon className="w-3.5 h-3.5" />
        </ToolbarBtn>
        <ToolbarBtn onClick={() => imageInputRef.current?.click()} title="Upload image">
          <Upload className="w-3.5 h-3.5" />
        </ToolbarBtn>
        <input ref={imageInputRef} type="file" accept="image/*" onChange={handleImageUpload} className="hidden" />

        {/* Table */}
        <ToolbarBtn active={editor.isActive("table")} onClick={insertTable} title="Insert table">
          <TableIcon className="w-3.5 h-3.5" />
        </ToolbarBtn>

        {/* Table controls (shown when cursor is inside a table) */}
        {editor.isActive("table") && (
          <>
            <Separator orientation="vertical" className="h-5 mx-1" />
            <Button variant="ghost" size="sm" className="h-7 text-xs px-2" type="button"
              onClick={() => editor.chain().focus().addColumnAfter().run()} title="Add column after">
              +Col
            </Button>
            <Button variant="ghost" size="sm" className="h-7 text-xs px-2" type="button"
              onClick={() => editor.chain().focus().addRowAfter().run()} title="Add row after">
              +Row
            </Button>
            <Button variant="ghost" size="sm" className="h-7 text-xs px-2 text-destructive" type="button"
              onClick={() => editor.chain().focus().deleteTable().run()} title="Delete table">
              ✕Tbl
            </Button>
          </>
        )}
      </div>

      {/* ── Editor body ──────────────────────────────────────────────────────── */}
      <div className="flex-1 overflow-auto">
        <EditorContent
          editor={editor}
          className="min-h-[500px] [&_.ProseMirror]:p-6 [&_.ProseMirror]:min-h-[500px] [&_.ProseMirror]:focus:outline-none [&_.ProseMirror]:prose [&_.ProseMirror]:prose-sm [&_.ProseMirror]:dark:prose-invert [&_.ProseMirror]:max-w-none [&_.ProseMirror_p.is-editor-empty:first-child::before]:text-muted-foreground [&_.ProseMirror_p.is-editor-empty:first-child::before]:content-[attr(data-placeholder)] [&_.ProseMirror_p.is-editor-empty:first-child::before]:float-left [&_.ProseMirror_p.is-editor-empty:first-child::before]:pointer-events-none [&_.ProseMirror_p.is-editor-empty:first-child::before]:h-0 [&_.ProseMirror_table]:border-collapse [&_.ProseMirror_td]:border [&_.ProseMirror_td]:border-border [&_.ProseMirror_td]:p-2 [&_.ProseMirror_th]:border [&_.ProseMirror_th]:border-border [&_.ProseMirror_th]:p-2 [&_.ProseMirror_th]:bg-muted [&_.ProseMirror_a]:text-primary [&_.ProseMirror_a]:underline [&_.ProseMirror_blockquote]:border-l-4 [&_.ProseMirror_blockquote]:border-primary/40 [&_.ProseMirror_blockquote]:pl-4 [&_.ProseMirror_blockquote]:text-muted-foreground [&_.ProseMirror_hr]:border-border"
        />
      </div>

      {/* ── Link dialog ───────────────────────────────────────────────────────── */}
      <Dialog open={showLinkDialog} onOpenChange={setShowLinkDialog}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Insert Link</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 pt-2">
            <div className="space-y-1">
              <Label className="text-xs">URL</Label>
              <Input
                value={linkUrl}
                onChange={(e) => setLinkUrl(e.target.value)}
                placeholder="https://example.com"
                onKeyDown={(e) => e.key === "Enter" && applyLink()}
                autoFocus
              />
            </div>
            {linkText && (
              <p className="text-xs text-muted-foreground">
                Selected text: <span className="font-medium">{linkText}</span>
              </p>
            )}
            <div className="flex justify-end gap-2">
              <Button variant="outline" size="sm" onClick={() => setShowLinkDialog(false)}>Cancel</Button>
              {editor.isActive("link") && (
                <Button variant="outline" size="sm" onClick={() => {
                  editor.chain().focus().unsetLink().run();
                  setShowLinkDialog(false);
                }}>
                  Remove link
                </Button>
              )}
              <Button size="sm" onClick={applyLink}>Apply</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
